import { z } from "zod";
import { timestampSchema, uuidSchema } from "./api.ts";
import type { Permission } from "./permissions.ts";

/**
 * Shared DTO building blocks + contract conventions for every module.
 *
 * CONVENTIONS (read before writing a contract or a route):
 * 1. `xxxInputSchema` / `xxxQuerySchema` / `xxxParamsSchema` are PARSED by the API
 *    validator (and by web forms through zodResolver). They may use `.strict()`,
 *    defaults, trims and transforms.
 * 2. Response DTO schemas (`xxxSchema`, no "Input" suffix) are ENCODED by the API
 *    serializer (fastify-type-provider-zod 7 calls `z.safeEncode`). Therefore DTOs
 *    - contain NO `.transform()` (encode throws on unidirectional transforms),
 *    - do not rely on `.default()` (defaults are not applied when encoding; every
 *      key must be present, use `null` rather than omitting a key),
 *    - are NOT `.strict()` (extra DB-row fields are stripped, not rejected),
 *    - carry timestamps as ISO-8601 strings (convert `Date` with `toISOString()`).
 * 3. PATCH inputs are built from shapes WITHOUT defaults: in zod 4 `.partial()`
 *    still applies an inner `.default()`, which would silently overwrite fields.
 * 4. PATCH uses JSON-merge-patch semantics: omitted key = unchanged, `null` =
 *    clear (for nullable fields), nested objects are merged key by key unless a
 *    doc comment says "replaced as a whole".
 * 5. Money is `*Minor` integers (+ currency), ids are uuid v7 strings.
 * 6. Lists: tables use offset pages (`pageSchema`), append-only feeds use cursors
 *    (`cursorPageSchema`), small admin collections use `listResponseSchema`.
 */

// ---------------------------------------------------------------------------
// Entity types
// ---------------------------------------------------------------------------

/**
 * Business record types of the whole product. Used by search, files,
 * custom fields, saved views, activity timelines and realtime invalidation.
 */
export const ENTITY_TYPES = [
  "lead",
  "client",
  "company",
  "contact",
  "proposal",
  "contract",
  "invoice",
  "payment",
  "expense",
  "project",
  "task",
  "time_entry",
  "file",
  "form",
  "meeting",
  "template",
  "automation",
  "comment",
  "credit_note",
  "service",
  "activity",
  "follow_up",
  /** A creative item going through proofing and approval (PRD §8 Item 11). */
  "deliverable",
  /** One planned post on the content calendar (US-11.1). */
  "content_item",
] as const;
export type EntityType = (typeof ENTITY_TYPES)[number];
export const entityTypeSchema = z.enum(ENTITY_TYPES);

/** Workspace / platform records that appear in the audit log and realtime events but not in search. */
export const SYSTEM_ENTITY_TYPES = [
  "org",
  "user",
  "member",
  "invitation",
  "role",
  "api_key",
  "custom_field",
  "saved_view",
  "notification",
  "pipeline",
  "integration",
  "webhook",
  "message_template",
] as const;
export type SystemEntityType = (typeof SYSTEM_ENTITY_TYPES)[number];
export const systemEntityTypeSchema = z.enum(SYSTEM_ENTITY_TYPES);

/** Every type that can be audited or invalidated over realtime. */
export const ALL_ENTITY_TYPES = [...ENTITY_TYPES, ...SYSTEM_ENTITY_TYPES] as const;
export type AnyEntityType = EntityType | SystemEntityType;
export const anyEntityTypeSchema = z.enum(ALL_ENTITY_TYPES);

export function isBusinessEntityType(t: string): t is EntityType {
  return (ENTITY_TYPES as readonly string[]).includes(t);
}

export const ENTITY_LABELS: Record<AnyEntityType, { singular: string; plural: string }> = {
  lead: { singular: "Lead", plural: "Leads" },
  client: { singular: "Client", plural: "Clients" },
  company: { singular: "Company", plural: "Companies" },
  contact: { singular: "Contact", plural: "Contacts" },
  proposal: { singular: "Proposal", plural: "Proposals" },
  contract: { singular: "Contract", plural: "Contracts" },
  invoice: { singular: "Invoice", plural: "Invoices" },
  payment: { singular: "Payment", plural: "Payments" },
  expense: { singular: "Expense", plural: "Expenses" },
  project: { singular: "Project", plural: "Projects" },
  task: { singular: "Task", plural: "Tasks" },
  time_entry: { singular: "Time entry", plural: "Time entries" },
  file: { singular: "File", plural: "Files" },
  form: { singular: "Form", plural: "Forms" },
  meeting: { singular: "Meeting", plural: "Meetings" },
  template: { singular: "Template", plural: "Templates" },
  automation: { singular: "Automation", plural: "Automations" },
  comment: { singular: "Comment", plural: "Comments" },
  credit_note: { singular: "Credit note", plural: "Credit notes" },
  service: { singular: "Service", plural: "Services" },
  activity: { singular: "Activity", plural: "Activities" },
  follow_up: { singular: "Follow-up", plural: "Follow-ups" },
  deliverable: { singular: "Deliverable", plural: "Deliverables" },
  content_item: { singular: "Content item", plural: "Content calendar" },
  org: { singular: "Organization", plural: "Organizations" },
  user: { singular: "User", plural: "Users" },
  member: { singular: "Member", plural: "Members" },
  invitation: { singular: "Invitation", plural: "Invitations" },
  role: { singular: "Role", plural: "Roles" },
  api_key: { singular: "API key", plural: "API keys" },
  custom_field: { singular: "Custom field", plural: "Custom fields" },
  saved_view: { singular: "Saved view", plural: "Saved views" },
  notification: { singular: "Notification", plural: "Notifications" },
  pipeline: { singular: "Pipeline", plural: "Pipelines" },
  integration: { singular: "Integration", plural: "Integrations" },
  webhook: { singular: "Webhook", plural: "Webhooks" },
  message_template: { singular: "Message template", plural: "Message templates" },
};

/**
 * Canonical WEB routes (the React router MUST serve these). The API uses them to
 * build `url` fields in search results, notifications and emails. `:id` is
 * replaced by the record id; `null` = no standalone page (e.g. comments, whose
 * url is built by the owning module as `<parent url>#comment-<id>`).
 */
export const ENTITY_ROUTES: Record<AnyEntityType, string | null> = {
  lead: "/leads/:id",
  client: "/clients/:id",
  company: "/companies/:id",
  contact: "/contacts/:id",
  proposal: "/proposals/:id",
  contract: "/contracts/:id",
  invoice: "/invoices/:id",
  payment: "/payments/:id",
  expense: "/expenses/:id",
  project: "/projects/:id",
  task: "/tasks/:id",
  time_entry: "/time?entry=:id",
  file: "/files/:id",
  form: "/forms/:id",
  meeting: "/calendar?meeting=:id",
  template: "/templates/:id",
  automation: "/automations/:id",
  comment: null,
  credit_note: "/credit-notes/:id",
  service: "/services/:id",
  // An activity has no page of its own: it is rendered on the timeline of the
  // record it is linked to, so the owning module supplies the url (`…#activity-<id>`).
  activity: null,
  follow_up: "/follow-ups",
  deliverable: "/proofs/:id",
  content_item: "/calendar?item=:id",
  org: "/settings/organization",
  user: "/settings/profile",
  member: "/settings/members",
  invitation: "/settings/members?tab=invitations",
  role: "/settings/roles",
  api_key: "/settings/api-keys",
  custom_field: "/settings/custom-fields",
  saved_view: null,
  notification: "/notifications",
  pipeline: "/settings/pipelines",
  integration: "/settings/integrations",
  webhook: "/settings/webhooks",
  message_template: "/settings/message-templates",
};

/** Web path of a record, e.g. entityPath("client", id) → "/clients/<id>". */
export function entityPath(entityType: AnyEntityType, id?: string | null): string | null {
  // Callers feed this server data (audit rows are append-only and may name a
  // retired entity type), so an unknown key must degrade to "no link" rather
  // than throw and take the whole page down.
  const route = ENTITY_ROUTES[entityType] as string | null | undefined;
  if (!route) return null;
  if (!route.includes(":id")) return route;
  if (!id) return null;
  return route.replace(":id", encodeURIComponent(id));
}

/**
 * Permission needed to SEE records of a type (search filtering, realtime fan-out,
 * activity timelines). `null` = any active member of the org (row-level rules,
 * e.g. project membership or notification recipient, still apply).
 */
export const ENTITY_READ_PERMISSION: Record<AnyEntityType, Permission | null> = {
  lead: "leads:read",
  client: "clients:read",
  company: "clients:read",
  contact: "clients:read",
  proposal: "proposals:read",
  contract: "contracts:read",
  invoice: "invoices:read",
  payment: "invoices:read",
  expense: "expenses:read",
  project: "projects:read",
  task: "projects:read",
  time_entry: "time:track",
  file: "files:read",
  form: "forms:manage",
  meeting: null,
  template: "projects:read",
  automation: "automations:manage",
  comment: null,
  credit_note: "invoices:read",
  service: null,
  // Activities and follow-ups are gated per ROW, not per type: you see the ones
  // linked to a record you can read, subject to the activity's own visibility
  // (US-6.7). The `activities` module narrows this with `searchPermissions`.
  activity: null,
  follow_up: null,
  // A proof and a planned post are delivery records: you see them if you can
  // see projects, and the owning modules narrow that per row (project
  // membership) exactly as the projects module does.
  deliverable: "projects:read",
  content_item: "projects:read",
  org: null,
  user: null,
  member: null,
  invitation: "members:manage",
  role: null,
  api_key: "integrations:manage",
  custom_field: null,
  saved_view: null,
  notification: null,
  pipeline: "leads:read",
  integration: "integrations:manage",
  webhook: "integrations:manage",
  message_template: null,
};

export const entityRefSchema = z.object({ entityType: entityTypeSchema, entityId: uuidSchema });
export type EntityRef = z.infer<typeof entityRefSchema>;

// ---------------------------------------------------------------------------
// People / actors
// ---------------------------------------------------------------------------

/** Compact user reference embedded in DTOs (assignee, owner, uploader…). */
export const userRefSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  avatarUrl: z.string().nullable(),
});
export type UserRef = z.infer<typeof userRefSchema>;

/**
 * Who performed an action. `ai` = the OpenAI-backed "Ask your business" assistant
 * acting on a user's behalf (the user is recorded in audit `changes`/metadata);
 * `automation` = workflow automation; `system` = jobs, migrations, schedulers.
 */
export const ACTOR_TYPES = ["user", "api_key", "portal_contact", "system", "automation", "ai"] as const;
export type ActorType = (typeof ACTOR_TYPES)[number];
export const actorTypeSchema = z.enum(ACTOR_TYPES);

/** `id` is null for `system`; `avatarUrl` only for users/portal contacts (may be omitted). */
export const actorSchema = z.object({
  type: actorTypeSchema,
  id: uuidSchema.nullable(),
  name: z.string(),
  avatarUrl: z.string().nullable().optional(),
});
export type Actor = z.infer<typeof actorSchema>;

// ---------------------------------------------------------------------------
// Scalars
// ---------------------------------------------------------------------------

/** ISO-3166-1 alpha-2 country code ("IN", "US", "AE"). */
export const countryCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{2}$/, "Use a 2-letter ISO country code");

export function isHttpUrl(value: string): boolean {
  try {
    const u = new URL(value);
    return (u.protocol === "http:" || u.protocol === "https:") && u.hostname.length > 0;
  } catch {
    return false;
  }
}
/** Absolute http(s) URL, max 2048 chars (javascript:, data: etc. rejected). */
export const httpUrlSchema = z.string().trim().max(2048).refine(isHttpUrl, "Enter a valid http(s) URL");

export function isValidTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}
/** IANA time zone ("Asia/Kolkata"). */
export const timezoneSchema = z.string().trim().min(1).max(64).refine(isValidTimeZone, "Unknown time zone");

/** 24h wall-clock time "HH:mm". */
export const hhmmSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use HH:mm (24h)");

/** 0 = Sunday … 6 = Saturday (same as Date#getDay and date-fns). */
export const weekdaySchema = z.number().int().min(0).max(6);

export const timestampsShape = { createdAt: timestampSchema, updatedAt: timestampSchema };
export const timestampsSchema = z.object(timestampsShape);
export type Timestamps = z.infer<typeof timestampsSchema>;

// ---------------------------------------------------------------------------
// Address
// ---------------------------------------------------------------------------

/**
 * Postal address. Used both as input and in DTOs (all keys nullable/optional;
 * the API returns every key, null when empty). Replaced as a whole on PATCH.
 */
export const addressSchema = z.object({
  line1: z.string().trim().max(200).nullable().optional(),
  line2: z.string().trim().max(200).nullable().optional(),
  city: z.string().trim().max(100).nullable().optional(),
  state: z.string().trim().max(100).nullable().optional(),
  postalCode: z.string().trim().max(20).nullable().optional(),
  country: countryCodeSchema.nullable().optional(),
});
export type Address = z.infer<typeof addressSchema>;

// ---------------------------------------------------------------------------
// Input helpers
// ---------------------------------------------------------------------------

/**
 * INPUT-ONLY wrapper for optional text form fields: trims, maps "" to null, then
 * validates with `inner`. Input type `string | null`, output `inner | null`.
 * Lets react-hook-form send empty inputs without per-field massaging.
 */
export function blankToNull<T extends z.ZodType<unknown, string>>(inner: T) {
  return z.union([
    z.null(),
    z
      .string()
      .trim()
      .transform((v) => (v === "" ? null : v))
      .pipe(z.union([z.null(), inner])),
  ]);
}

/**
 * INPUT-ONLY boolean query-string param: accepts true/false and "true"/"false"/"1"/"0"
 * (URLSearchParams stringifies booleans, so the web can pass real booleans).
 */
export const queryBooleanSchema = z
  .union([z.boolean(), z.enum(["true", "false", "1", "0"])])
  .transform((v) => v === true || v === "true" || v === "1");

/**
 * INPUT-ONLY multi-value query param: accepts "a,b,c" or repeated keys (?t=a&t=b).
 */
export function csvArraySchema<T extends z.ZodType>(item: T, max: number) {
  return z.preprocess(
    (v) =>
      typeof v === "string"
        ? v
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : v,
    z.array(item).max(max),
  );
}

/** True when every element is unique (for `.refine` on id/permission arrays). */
export function isUnique(values: readonly unknown[]): boolean {
  return new Set(values).size === values.length;
}

// ---------------------------------------------------------------------------
// Collections
// ---------------------------------------------------------------------------

/** Unpaginated small collection (roles, invitations, API keys, custom fields, views). */
export function listResponseSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({ items: z.array(item) });
}
export interface ListResponse<T> {
  items: T[];
}
